/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ChevronDown, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
    addMonths,
    buildCalendarDays,
    getWeekdayLabels,
    parseIsoDateString,
} from '@/components/date-picker/date-picker.utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/buttons';
import { useBasket } from '@/providers/basket';
import { resolveFormattingLocale } from '@/lib/formatting-locale';
import { useCheckoutActions } from '@/hooks/use-checkout-actions';
import { useAutoPopulateDeliverySchedule } from '@/hooks/use-auto-populate-delivery-schedule';
import { getDeliveryShipment } from '@/extensions/bopis/lib/basket-utils';
import { getBasketBoolean, getShipmentString, normaliseDeliveryWindows } from '@/lib/checkout/ohds-basket-utils';
import { cn } from '@/lib/utils';

/**
 * Test/Storybook seam — the slice of `useCheckoutActions` consumed by the
 * inline picker. Letting callers inject a stub keeps the picker testable
 * without standing up the full checkout actions provider tree.
 */
export interface HdMethodCardDatePickerActions {
    submitDeliverySchedule: (selectedDate: string | null, options?: { isUserSelected?: boolean }) => void;
    deliveryScheduleFetcher: { state: 'idle' | 'loading' | 'submitting'; data?: { success?: boolean } | null };
}

export interface HdMethodCardDatePickerProps {
    className?: string;
    /**
     * Disambiguates the picker when two instances are rendered for the same
     * HD line item (one inside each radio option — White Glove + Doorstep).
     * The key is appended to every `data-testid` (e.g.
     * `hd-method-card-date-picker-hd`) and to the popover trigger's
     * `id`/`aria-controls` link so screen readers do not mismatch them.
     * Unset → no suffix is added (keeps backward compatibility with
     * standalone Storybook usage).
     */
    methodKey?: 'hd' | 'thd';
    /**
     * Whether this instance is allowed to fire the FE-03 auto-populate PATCH
     * on first render. The two-instance mount (HD + THD inside one HD line
     * item) must set this to `true` on only ONE instance (the HD one) to
     * avoid sending duplicate basket PATCHes — both pickers share the same
     * basket-derived persisted date, so a single auto-populate covers both.
     * Defaults to `true` so standalone callers (Storybook, tests) get the
     * auto-populate behaviour by default.
     */
    autoPopulate?: boolean;
    /**
     * When `true`, the picker renders as a non-interactive mirror of the
     * basket's `c_requestedDeliveryDatetime` — the "Change date" link and
     * popover trigger are suppressed, and the displayed date is sourced
     * from the persisted basket value rather than this instance's local
     * draft state.
     *
     * Used by the two-instance mount inside `DeliveryMethodToggle` so the
     * NON-selected method tile cannot mislead the shopper with a date
     * that won't actually ship. The shipment carries a single
     * `c_requestedDeliveryDatetime` — only the SELECTED method's picker
     * is allowed to edit it; the other one displays the active value
     * read-only so both tiles agree on what will be delivered.
     */
    isReadOnly?: boolean;
    /**
     * Test/Storybook seam: lets stories render the picker against a stubbed
     * basket without standing up `<BasketProvider/>`. Production callers
     * leave this unset so the live `useBasket()` is used.
     */
    useBasketHook?: typeof useBasket;
    /**
     * Test/Storybook seam: stub for the checkout-actions slice. Default
     * uses the real `useCheckoutActions` hook so production callers do
     * not need to wire anything.
     */
    useActions?: () => HdMethodCardDatePickerActions;
}

/**
 * Pure presentational calendar that renders inside a Radix `<PopoverContent>`.
 *
 * Extracted from {@link HdMethodCardDatePickerContent} so the same calendar
 * UI can be mounted from other surfaces (notably `DeliveryMethodToggle`'s
 * per-tile "Change date" popover added in FE-08) without dragging the
 * picker's summary line, trigger button, basket reads, or auto-populate
 * effect along with it.
 *
 * Fully controlled: every piece of state — `displayMonth`, `selectedDate`,
 * the day-select handler — lives in the caller. This preserves
 * `HdMethodCardDatePickerContent`'s existing per-instance lifecycle byte-for-
 * byte (the parent already owned `displayMonth`/`draftDate` state), and gives
 * FE-08 callers full control over how the popover interacts with their own
 * action wiring.
 *
 * @see HdMethodCardDatePickerContent for the canonical caller that bundles
 * this calendar with the summary line + auto-populate effect.
 */
export interface HdMethodCardDatePickerPopoverContentProps {
    /** Sorted list of ISO yyyy-MM-dd strings the shopper may pick from. */
    availableDates: string[];
    /** Currently highlighted day. `null` renders no selection but still shows the calendar. */
    selectedDate: string | null;
    /** Month currently being viewed in the calendar (controlled). */
    displayMonth: Date;
    /** Invoked when the shopper clicks the prev / next month chevrons. */
    onDisplayMonthChange: (next: Date) => void;
    /** Invoked with the ISO yyyy-MM-dd of the day the shopper clicked. */
    onSelect: (iso: string) => void;
    /** Disables the day cells (e.g. while a PATCH is in flight). */
    isSubmitting?: boolean;
    /**
     * Disambiguates the testid suffix when multiple instances of the calendar
     * are mounted in the same DOM (e.g. one popover per HD/THD tile).
     * Mirrors the suffixing strategy used by {@link HdMethodCardDatePickerContent}.
     */
    methodKey?: 'hd' | 'thd';
    className?: string;
}

export function HdMethodCardDatePickerPopoverContent({
    availableDates,
    selectedDate,
    displayMonth,
    onDisplayMonthChange,
    onSelect,
    isSubmitting = false,
    methodKey,
    className,
}: HdMethodCardDatePickerPopoverContentProps) {
    const { t, i18n } = useTranslation('checkout');
    const tid = (id: string) => `hd-method-card-date-picker${id}${methodKey ? `-${methodKey}` : ''}`;

    const formatDateLong = (iso: string | null): string | null => {
        const parsed = parseIsoDateString(iso ?? undefined);
        if (!parsed) return null;
        return parsed.toLocaleDateString(resolveFormattingLocale(i18n.language), {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const availableDateSet = new Set(availableDates);
    const minDate = availableDates[0];
    const maxDate = availableDates[availableDates.length - 1];
    const calendarDays = buildCalendarDays(displayMonth, selectedDate ?? undefined, minDate, maxDate, availableDateSet);
    const weekdayLabels = getWeekdayLabels(i18n.language);

    const canPreviousMonth = parseIsoDateString(minDate)
        ? addMonths(displayMonth, -1).getTime() >=
          new Date(
              (parseIsoDateString(minDate) as Date).getFullYear(),
              (parseIsoDateString(minDate) as Date).getMonth(),
              1,
              12
          ).getTime()
        : false;
    const canNextMonth = parseIsoDateString(maxDate)
        ? addMonths(displayMonth, 1).getTime() <=
          new Date(
              (parseIsoDateString(maxDate) as Date).getFullYear(),
              (parseIsoDateString(maxDate) as Date).getMonth(),
              1,
              12
          ).getTime()
        : false;

    return (
        <div className={className}>
            <div className="border-b border-border-subtle px-3 pt-3 pb-2">
                <p className="mb-2 text-sm font-semibold text-foreground">{t('deliverySchedule.popoverHeading')}</p>
                <div className="flex items-center justify-between gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="rounded-sm hover:bg-secondary"
                        aria-label={t('deliverySchedule.calendar.previousMonth')}
                        disabled={!canPreviousMonth}
                        onClick={() => onDisplayMonthChange(addMonths(displayMonth, -1))}
                        data-testid={tid('-prev-month')}>
                        <ChevronLeftIcon className="size-4" />
                    </Button>
                    <span
                        className="flex-1 text-center text-sm font-medium text-foreground"
                        data-testid={tid('-month-label')}>
                        {displayMonth.toLocaleDateString(resolveFormattingLocale(i18n.language), {
                            month: 'long',
                            year: 'numeric',
                        })}
                    </span>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="rounded-sm hover:bg-secondary"
                        aria-label={t('deliverySchedule.calendar.nextMonth')}
                        disabled={!canNextMonth}
                        onClick={() => onDisplayMonthChange(addMonths(displayMonth, 1))}
                        data-testid={tid('-next-month')}>
                        <ChevronRightIcon className="size-4" />
                    </Button>
                </div>
            </div>
            <div className="flex flex-col gap-2 p-3">
                <div className="grid grid-cols-7 gap-0">
                    {weekdayLabels.map((label) => (
                        <span
                            key={label}
                            className="text-center text-xs leading-none text-muted-foreground"
                            data-testid={tid('-weekday')}>
                            {label}
                        </span>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-y-1" role="grid" aria-label={t('deliverySchedule.popoverHeading')}>
                    {calendarDays.map((day) => {
                        const dayAriaLabel = formatDateLong(day.isoValue) ?? day.isoValue;
                        return (
                            <Button
                                key={day.isoValue}
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                disabled={day.isDisabled || isSubmitting}
                                aria-disabled={day.isDisabled || isSubmitting}
                                aria-label={t('deliverySchedule.calendar.chooseDay', {
                                    date: dayAriaLabel,
                                })}
                                aria-pressed={day.isSelected}
                                className={cn(
                                    'size-8 rounded-sm text-sm font-normal shadow-none hover:bg-secondary',
                                    day.isSelected &&
                                        'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground',
                                    !day.isCurrentMonth && 'opacity-50',
                                    day.isToday && !day.isSelected && 'border border-border'
                                )}
                                onClick={() => onSelect(day.isoValue)}
                                data-testid={tid(`-day-${day.isoValue}`)}>
                                {day.label}
                            </Button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/**
 * INT-12 (Sprint N+1) — Inline OHDS date picker that bundles the calendar
 * (`HdMethodCardDatePickerPopoverContent` above) with a "Get it by …" summary
 * line, a "Change date" trigger, the FE-03 auto-populate effect, and the
 * FE-04 master-kill-switch gate. Originally rendered inside each radio option
 * of `DeliveryMethodToggle`.
 *
 * IMPORTANT — current production status (post-FE-08):
 *   • The live checkout NO LONGER mounts this component. PR #114 replaced
 *     the radio-option scaffolding with `<RadioDeliveryMethodTile/>`, and
 *     FE-08 then wired OHDS by mounting `HdMethodCardDatePickerPopoverContent`
 *     (the extracted calendar above) directly inside `DeliveryMethodToggle`'s
 *     popover — see `delivery-method-toggle.tsx`. The auto-populate effect
 *     was lifted to `useAutoPopulateDeliverySchedule` and is called from the
 *     toggle.
 *   • The toggle's popover does NOT gate open/close on `c_ohdsEnabled`; it
 *     always opens and adapts the body. The FE-04 short-circuit below only
 *     fires when THIS component is mounted (currently: Storybook stories
 *     and any future caller that wants the bundled composition).
 *
 * FE-02 — inline render + popover anchor on the HD card.
 * FE-03 — auto-populates `displayedDate` from `c_deliveryWindows[0]` on first
 *   render and PATCHes the basket with `isUserSelected=false` so the SYSTEM
 *   fallback contract is preserved (`onlineDeliveryScheduledBy = "System"`).
 * FE-04 — the entire subtree is suppressed when `basket.c_ohdsEnabled === false`,
 *   honouring the BE-01 master kill switch.
 *
 * Empty `c_deliveryWindows` (e.g. ineligible ZIP) collapses the surface to a
 * single line of `"We'll contact you to schedule your delivery."` copy with
 * the `Change date` affordance suppressed — there are no dates to pick from.
 */
function HdMethodCardDatePickerContent({
    className,
    methodKey,
    autoPopulate = true,
    isReadOnly = false,
    useBasketHook = useBasket,
    useActions = useCheckoutActions,
}: HdMethodCardDatePickerProps) {
    const { t, i18n } = useTranslation('checkout');
    const basket = useBasketHook();
    const { submitDeliverySchedule, deliveryScheduleFetcher } = useActions();
    // Suffix every test id with the methodKey when present so two coexisting
    // instances (HD + THD radio options on the same line item) can be queried
    // independently by Playwright / RTL without ambiguity.
    const tid = (id: string) => `hd-method-card-date-picker${id}${methodKey ? `-${methodKey}` : ''}`;

    // Reuse the canonical delivery-shipment lookup so BOPIS baskets (where
    // shipments[0] may be the store-pickup shipment) read OHDS state from
    // the SHIPPED portion only. Returns `undefined` if the basket has no
    // delivery items — we render nothing in that case (no OHDS surface).
    const deliveryShipment = getDeliveryShipment(basket ?? undefined);
    const persistedDate = getShipmentString(deliveryShipment, 'c_requestedDeliveryDatetime');
    // Memoise on the RAW `c_deliveryWindows` property reference, which is
    // stable across renders while the basket reference is stable (the
    // shipment object is the same identity returned by `find()`). The old
    // version keyed on the shipment WRAPPER, which broke when callers
    // produced a fresh object per render.
    const rawWindows = (deliveryShipment as unknown as Record<string, unknown> | undefined)?.c_deliveryWindows;
    const availableDates = useMemo(() => normaliseDeliveryWindows(rawWindows).sort(), [rawWindows]);
    const ohdsEnabled = getBasketBoolean(basket, 'c_ohdsEnabled');

    const [isOpen, setIsOpen] = useState(false);
    const [draftDate, setDraftDate] = useState<string | null>(persistedDate ?? availableDates[0] ?? null);
    const [displayMonth, setDisplayMonth] = useState(() => {
        const seed = parseIsoDateString(persistedDate ?? availableDates[0] ?? undefined);
        return seed ? new Date(seed.getFullYear(), seed.getMonth(), 1, 12) : new Date();
    });
    // Per-instance fingerprint of `availableDates`. When this changes (a
    // real structural shift — new ZIP, BE-02 window invalidation), the
    // SELECTED picker re-seeds its local draft from the basket. Plain
    // basket-date PATCHes keep this key stable so an in-flight draft
    // doesn't snap back during the round-trip.
    //
    // The read-only mirror (the non-selected method's instance) bypasses
    // local draft entirely (`displayDate` reads `persistedDate` directly),
    // so this machinery is a no-op there but still cheap to compute.
    const seedKey = availableDates.join(',');
    const lastSeedKeyRef = useRef(seedKey);

    // FE-03 — fire-once auto-populate PATCH on first render. Delegated to
    // `useAutoPopulateDeliverySchedule` so the same SYSTEM-date contract
    // can also be honoured by callers that don't render the picker itself
    // (e.g. `DeliveryMethodToggle` after the PR #114 tile-shell refactor).
    // We forward the existing `useBasketHook` / `useActions` test seams so
    // Storybook stories and unit tests keep their stub injection points.
    useAutoPopulateDeliverySchedule({
        enabled: autoPopulate,
        useBasketHook,
        useActions,
    });

    // Re-seed local state ONLY when availableDates structurally changes
    // (new ZIP / BE-02 invalidation), NOT on every basket PATCH. This keeps
    // the WG and Doorstep picker instances visually independent — picking a
    // date in one no longer cascades into the other's "Get it by" line.
    // The basket still carries the most recent PATCHed date (single source
    // of truth for order placement); only the per-picker DISPLAY state
    // diverges intentionally.
    useEffect(() => {
        if (lastSeedKeyRef.current === seedKey) return;
        lastSeedKeyRef.current = seedKey;
        const next = persistedDate ?? availableDates[0] ?? null;
        setDraftDate(next);
        const seed = parseIsoDateString(next ?? undefined);
        if (seed) {
            setDisplayMonth(new Date(seed.getFullYear(), seed.getMonth(), 1, 12));
        }
    }, [seedKey, persistedDate, availableDates]);

    // FE-04 — master kill switch off → render nothing. Hides the entire
    // subtree (line + popover trigger) so a c_ohdsEnabled=false sandbox
    // checks out exactly like a non-OHDS site.
    if (!ohdsEnabled) {
        return null;
    }

    const formatDateLong = (iso: string | null): string | null => {
        const parsed = parseIsoDateString(iso ?? undefined);
        if (!parsed) return null;
        return parsed.toLocaleDateString(resolveFormattingLocale(i18n.language), {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const noDates = availableDates.length === 0;
    // Read-only mirror sources its display value from the basket's persisted
    // date (the single source of truth for what will actually ship). The
    // editable instance reads from local `draftDate` so the UI tracks the
    // shopper's selection during the PATCH round-trip. Both fall back to
    // `availableDates[0]` so the read-only mirror doesn't render an empty
    // line during the brief window between mount and the FE-03
    // auto-populate PATCH completing.
    const displayDate = isReadOnly ? (persistedDate ?? availableDates[0] ?? null) : draftDate;
    const formattedDate = formatDateLong(displayDate);

    if (noDates) {
        // Eligible HD card but the basket carries no scheduling windows
        // (e.g. ZIP went ineligible after the address was set, or the OHDS
        // service returned an empty range). Surface the copy without the
        // change-date affordance — there is nothing to pick.
        return (
            <p
                className={cn('mt-1 flex items-center gap-2 text-xs text-muted-foreground', className)}
                data-testid={tid('-no-dates')}>
                <CalendarIcon className="size-4 shrink-0" aria-hidden="true" />
                <span>{t('deliverySchedule.noDatesAvailable')}</span>
            </p>
        );
    }

    const handleDaySelect = (iso: string) => {
        setDraftDate(iso);
        submitDeliverySchedule(iso, { isUserSelected: true });
        setIsOpen(false);
    };

    return (
        <div
            className={cn('mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs', className)}
            data-testid={tid('')}
            data-readonly={isReadOnly ? 'true' : undefined}>
            <CalendarIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-foreground" data-testid={tid('-summary')}>
                {formattedDate
                    ? t('deliverySchedule.getItBy', { date: formattedDate })
                    : t('deliverySchedule.noDatesAvailable')}
            </span>
            {!isReadOnly && (
                <>
                    <span aria-hidden="true" className="text-border">
                        |
                    </span>
                    <Popover open={isOpen} onOpenChange={setIsOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs font-medium text-primary underline-offset-4 hover:underline"
                                data-testid={tid('-trigger')}
                                aria-haspopup="dialog"
                                aria-expanded={isOpen}
                                disabled={deliveryScheduleFetcher.state === 'submitting'}>
                                {t('deliverySchedule.changeDate')}
                                <ChevronDown className="ml-1 size-3.5" aria-hidden="true" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="start"
                            sideOffset={8}
                            className="w-[19rem] rounded-md border-border p-0"
                            data-testid={tid('-popover')}>
                            <HdMethodCardDatePickerPopoverContent
                                availableDates={availableDates}
                                selectedDate={draftDate}
                                displayMonth={displayMonth}
                                onDisplayMonthChange={setDisplayMonth}
                                onSelect={handleDaySelect}
                                methodKey={methodKey}
                            />
                        </PopoverContent>
                    </Popover>
                </>
            )}
        </div>
    );
}

export default function HdMethodCardDatePicker(props: HdMethodCardDatePickerProps) {
    return <HdMethodCardDatePickerContent {...props} />;
}
